import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	type ArtemisTaskFilterInput,
	type ArtemisTaskSortInput,
	CancelTaskDocument,
	CreateTaskDocument,
	type CreateTaskInput,
	DeleteTaskDocument,
	DispatchTaskDocument,
	GetTaskDocument,
	GetTasksDocument,
	ReopenTaskDocument,
	SubmitTaskInputDocument,
	UpdateTaskDocument,
	type UpdateTaskInput,
} from "@/common/artemis-sdk/graphql";
import type { ArtemisTask } from "../model";
import { useGqlClient } from "../use-gql-client";
import { buildWhere, toOrder, usePagedList } from "./use-paged-list";

/**
 * Fetches the first page of tasks for a workspace (array form, for lookups/dropdowns).
 * For the paginated table use {@link useTasksPaged}.
 */
export function useTasks(workspaceId: string) {
	const gqlFetch = useGqlClient();

	return useQuery({
		queryKey: ["tasks", workspaceId],
		queryFn: async () => {
			const data = await gqlFetch(GetTasksDocument, {
				workspaceId,
				order: [{ createdAt: "DESC" }],
			});
			return (data.tasks?.nodes as ArtemisTask[]) ?? [];
		},
		enabled: !!workspaceId,
	});
}

/**
 * Server-side paginated task list for a workspace: Next/Prev cursor pagination plus
 * server-side search (name/id), status filter, and sort.
 */
export function useTasksPaged(workspaceId: string, initialStatus?: string) {
	const gqlFetch = useGqlClient();

	return usePagedList<ArtemisTask>({
		baseKey: ["tasks", workspaceId],
		enabled: !!workspaceId,
		initialSort: [{ id: "createdAt", desc: true }],
		initialFilters: { status: initialStatus },
		fetchPage: async ({ debouncedSearch, filters, sort, window }) => {
			const data = await gqlFetch(GetTasksDocument, {
				workspaceId,
				...window,
				order: toOrder<ArtemisTaskSortInput>(sort),
				where: buildWhere(debouncedSearch, ["name", "id"], filters) as
					| ArtemisTaskFilterInput
					| undefined,
			});
			const c = data.tasks;
			return {
				nodes: (c?.nodes as ArtemisTask[]) ?? [],
				totalCount: c?.totalCount ?? 0,
				pageInfo: c?.pageInfo ?? { hasNextPage: false, hasPreviousPage: false },
			};
		},
	});
}

/**
 * Fetches a single task by its ID.
 */
export function useTask(taskId: string) {
	const gqlFetch = useGqlClient();

	return useQuery({
		queryKey: ["task", taskId],
		queryFn: async () => {
			const data = await gqlFetch(GetTaskDocument, { taskId });
			return data.task as ArtemisTask;
		},
		enabled: !!taskId,
	});
}

/**
 * Creates a new task.
 */
export function useCreateTask() {
	const gqlFetch = useGqlClient();

	const { mutate } = useMutation({
		mutationFn: async (input: CreateTaskInput) => {
			const data = await gqlFetch(CreateTaskDocument, { input });
			return data.createTask.task as ArtemisTask;
		},
	});
	return mutate;
}

/** Dispatches a task by id — for callers (e.g. the create wizard) that only get the id after creation. */
export function useDispatchTaskById() {
	const gqlFetch = useGqlClient();

	const { mutate } = useMutation({
		mutationFn: async (taskId: string) => {
			const data = await gqlFetch(DispatchTaskDocument, { taskId });
			return data.dispatchTask.task;
		},
	});
	return mutate;
}

/** Updates an existing task. */
export function useUpdateTask(taskId: string) {
	const gqlFetch = useGqlClient();

	const queryClient = useQueryClient();
	const { mutate } = useMutation({
		mutationFn: async (input: UpdateTaskInput) => {
			const data = await gqlFetch(UpdateTaskDocument, { taskId, input });
			return data.updateTask.task;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["task", taskId] }),
	});
	return mutate;
}

/** Permanently deletes tasks. */
export function useDeleteTasks() {
	const gqlFetch = useGqlClient();

	return useMutation({
		mutationFn: async (taskIds: string[]) => {
			await Promise.all(
				taskIds.map((taskId) => gqlFetch(DeleteTaskDocument, { taskId })),
			);
		},
	});
}

/** Cancels a running task, sending a stop signal to its agent if one is running. */
export function useCancelTask(taskId: string) {
	const gqlFetch = useGqlClient();

	const queryClient = useQueryClient();
	const { mutate } = useMutation({
		mutationFn: async () => {
			const data = await gqlFetch(CancelTaskDocument, { taskId });
			return data.cancelTask.task;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["task", taskId] }),
	});
	return mutate;
}

/** Reopens a terminal task (Completed/Failed/Cancelled), resetting it to CREATED so it can be dispatched again. */
export function useReopenTask(taskId: string) {
	const gqlFetch = useGqlClient();

	const queryClient = useQueryClient();
	const { mutate } = useMutation({
		mutationFn: async () => {
			const data = await gqlFetch(ReopenTaskDocument, { taskId });
			return data.reopenTask.task;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["task", taskId] }),
	});
	return mutate;
}

/** Submits a user's response to a task that is awaiting input, recording it in the worklog. */
export function useSubmitTaskInput(taskId: string) {
	const gqlFetch = useGqlClient();

	const queryClient = useQueryClient();
	const { mutate } = useMutation({
		mutationFn: async (text: string) => {
			const data = await gqlFetch(SubmitTaskInputDocument, { taskId, text });
			return data.submitTaskInput.task;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["task", taskId] }),
	});
	return mutate;
}

/**
 * Dispatches a task to its assigned agent. The only way a task is ever sent to an agent — there
 * is no automatic dispatch (ADR-026). Valid from CREATED/WAITING (dependencies resolved) and
 * INPUT_REQUIRED (after submitting input via useSubmitTaskInput).
 */
export function useDispatchTask(taskId: string) {
	const gqlFetch = useGqlClient();

	const queryClient = useQueryClient();
	const { mutate, isPending } = useMutation({
		mutationFn: async () => {
			const data = await gqlFetch(DispatchTaskDocument, { taskId });
			return data.dispatchTask.task;
		},
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: ["task", taskId] }),
	});
	return { dispatchTask: mutate, isDispatching: isPending };
}
